import { GridWithGaps } from '@dreamstack/feature-components/src/layout/MainGrid'
import { SimpleIconButton } from '@dreamstack/feature-components/src/layout/SimpleIconButton'
import {
  HeadingH3,
  HeadingH4,
  Text_16_24,
} from '@dreamstack/feature-components/src/layout/Texts'
import { useTranslation } from '@dreamstack/i18n'
import { ArrowRight } from '@dreamstack/icons/src/icon-components/ArrowRight'
import { assertPopulatedDeep } from '@dreamstack/payload/src/lib/assertPopulated'
import type { BlockHighlightRealEstate } from '@dreamstack/payload/src/payload-types'
import { SimpleDefinitionList } from '@dreamstack/simple-components/src/lib/SimpleDefinitionList'
import { cn } from '@dreamstack/util/src/cn'
import { motion } from 'framer-motion'
import Image from 'next/image'
import Link from 'next/link'
import React from 'react'

const StyledHighlightCard: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  className,
  children,
  ...rest
}) => (
  <div
    className={cn(
      'bg-accentroGray-full text-accentroWhite-full p-px-16 -mt-px-8',
      'md:px-px-24 md:py-px-32 md:-mt-px-48 md:mr-px-32 md:ml-px-128',
      'ml:ml-px-16',
      'lg:ml-px-128 lg:-mt-px-80 lg:px-px-48',
      className
    )}
    {...rest}
  >
    {children}
  </div>
)

export type HighlightRealEstateProps = NonNullable<
  BlockHighlightRealEstate['entries']
>[number]

export const HighlightRealEstate = React.forwardRef<
  HTMLDivElement,
  { realEstate: NonNullable<BlockHighlightRealEstate['entries']>[number] }
>(({ realEstate }, ref) => {
  const t = useTranslation()
  if (!realEstate) return null
  assertPopulatedDeep(realEstate)
  const image = realEstate.image
  return (
    <GridWithGaps ref={ref}>
      <motion.div className="col-span-12">
        <div className="aspect-3/2 relative">
          <Image
            className="object-cover"
            src={image?.url ?? ''}
            alt={image?.alt || 'Property image'}
            fill
          />
        </div>
      </motion.div>
      <GridWithGaps className="col-span-full flex flex-col-reverse md:grid">
        <motion.div className="w-full relative col-start-1 col-span-12 row-start-auto">
          <StyledHighlightCard>
            <HeadingH3>{realEstate.title}</HeadingH3>
            <Text_16_24 className="mt-px-4">{realEstate.subTitle}</Text_16_24>
            <div className="grid grid-cols-2 gap-px-16 mt-px-32 md:grid-cols-4 lg:flex lg:flex-row lg:justify-between">
              {realEstate.rooms && (
                <SimpleDefinitionList
                  big
                  dt={
                    <HeadingH4 className="whitespace-nowrap">
                      {realEstate.rooms}
                    </HeadingH4>
                  }
                  dd={
                    <p className="text-px-16 leading-px-24 mt-px-4">
                      {t('accentro:room.rooms')}
                    </p>
                  }
                />
              )}
              {realEstate.squareMeter && (
                <SimpleDefinitionList
                  big
                  dt={
                    <HeadingH4 className="whitespace-nowrap">
                      {realEstate.squareMeter + ' '}
                      m²
                    </HeadingH4>
                  }
                  dd={
                    <p className="text-px-16 leading-px-24 mt-px-4">
                      {t('accentro:livingSpace')}
                    </p>
                  }
                />
              )}
              {(realEstate.price || realEstate.pricePerSquareMeter) && (
                <SimpleDefinitionList
                  big
                  dt={
                    <HeadingH4 className="whitespace-nowrap">
                      {realEstate.price + ' '}
                      EUR
                    </HeadingH4>
                  }
                  dd={
                    <p className="text-px-16 leading-px-24 mt-px-4">
                      {realEstate.pricePerSquareMeter + ' EUR /'}
                      <span>&nbsp;m²</span>
                    </p>
                  }
                />
              )}
              <div className="flex justify-end col-start-2 md:col-start-4">
                <Link
                  href={realEstate.slug ?? '#'}
                  passHref
                  className="text-accentroWhite-full"
                >
                  <SimpleIconButton color="inherit" aria-label="Go to property">
                    <ArrowRight className="text-accentroWhite-full h-px-32 w-px-32" />
                  </SimpleIconButton>
                </Link>
              </div>
            </div>
          </StyledHighlightCard>
        </motion.div>
      </GridWithGaps>
    </GridWithGaps>
  )
})

HighlightRealEstate.displayName = 'HighlightRealEstate'
